import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiBusinessService } from './api-business.service';
import { AuthenticationService } from './authentication.service';

/**
 * Slugs del catálogo de módulos (tabla `modules` del backend). Deben coincidir
 * con los del ModuleSeeder.
 */
export const MODULE_SEASON_PASSES = 'season_passes';
export const MODULE_CASH_REGISTER = 'cash_register';

/**
 * Módulos contratados por la empresa seleccionada, para ocultar secciones del
 * panel de gestor.
 *
 * Se leen de la propia empresa seleccionada (`/companies` → `modules[]`, con el
 * on/off en `pivot.active`), que es la única fuente válida para un superadmin
 * mirando una empresa que no es suya. `/auth/me` → companies[].modules queda
 * como respaldo para los objetos que no traen los módulos.
 *
 * Ojo: de momento el backend NO restringe nada a nivel de API, esto es solo
 * la capa visual.
 */
@Injectable({
  providedIn: 'root'
})
export class ModulesService {

  // null = sin restricción: no se ha podido saber qué módulos tiene la empresa
  // (no localizable en /auth/me, error de red...). En ese caso se muestra todo,
  // que es el comportamiento previo a los módulos.
  private activeModules = new BehaviorSubject<string[] | null>(null);
  public activeModules$ = this.activeModules.asObservable();

  private loadedCompanyId: number | null = null;

  constructor(
    private auth: AuthenticationService,
    private apiBusiness: ApiBusinessService,
  ) {
    // El selector de empresa (menu-panel) guarda aquí la empresa activa, tanto
    // en el arranque como al cambiarla siendo superadmin. Es un BehaviorSubject:
    // al suscribirse emite el valor actual.
    this.apiBusiness.businessStorage.subscribe((business) => this.applyBusiness(business));

    // Si nadie ha emitido todavía (recarga directa en una ruta del panel).
    if (this.loadedCompanyId === null) {
      this.applyBusiness(this.storedBusiness());
    }
  }

  hasModule(slug: string): boolean {
    const modules = this.activeModules.getValue();
    return !modules || modules.includes(slug);
  }

  private applyBusiness(business: any) {
    if (!business?.id) {
      return;
    }

    // Los módulos vienen en la propia empresa: se resuelve sin llamada.
    if (Array.isArray(business.modules)) {
      this.loadedCompanyId = business.id;
      this.activeModules.next(this.toActiveSlugs(business.modules));
      return;
    }

    if (business.id !== this.loadedCompanyId) {
      this.loadFromMyData(business.id);
    }
  }

  private loadFromMyData(companyId: number) {
    // Se marca antes de la llamada para no pedir /auth/me dos veces por la misma
    // empresa; si falla se vuelve a null y el siguiente cambio lo reintenta.
    this.loadedCompanyId = companyId;

    this.auth.getMyData().subscribe({
      next: (res: any) => {
        const company = res?.companies?.find((c: any) => c.id === companyId);

        this.activeModules.next(
          Array.isArray(company?.modules) ? this.toActiveSlugs(company.modules) : null,
        );
      },
      error: () => {
        this.loadedCompanyId = null;
        this.activeModules.next(null);
      },
    });
  }

  /**
   * Sin filas de módulos no se puede decidir nada (empresas anteriores al
   * ModuleSeeder), así que se muestra todo. Con filas, aunque estén todas
   * apagadas, se respeta lo que digan.
   */
  private toActiveSlugs(modules: any[]): string[] | null {
    if (modules.length === 0) {
      return null;
    }

    return modules
      .filter((module: any) => this.isActive(module))
      .map((module: any) => module.slug);
  }

  /**
   * Dos formas de la misma cosa:
   * - `/companies` (serialización cruda de Eloquent): el on/off real está en
   *   `pivot.active`; el `active` de primer nivel es el interruptor global del
   *   catálogo, siempre true.
   * - `/auth/me`: ya viene aplanado en `active`.
   */
  private isActive(module: any): boolean {
    const active = module?.pivot?.active ?? module?.active;
    return active === true || active === 1 || active === '1';
  }

  private storedBusiness(): any {
    const business = localStorage.getItem('business');
    return business ? JSON.parse(business) : null;
  }
}
